import { useEffect, useState } from 'react';
import { Card, Button, Badge, Modal, FormField, Spinner } from '../components/ui';

const LICENCE_TYPES = [
  { value: 'mandatory',  label: 'Mandatory HMO Licence (5+ tenants)' },
  { value: 'additional', label: 'Additional HMO Licence (3-4 tenants)' },
  { value: 'selective',  label: 'Selective Licence' },
];

const CERT_TYPES_HMO = [
  { value: 'fire_risk_assessment', label: 'Fire Risk Assessment', validityYears: 1 },
  { value: 'fire_alarm_bs5839',    label: 'Fire Alarm Test (BS5839)', validityYears: 1 },
];

const STATUS_COLORS = {
  active:  'valid',
  expired: 'expired',
  pending: 'due_soon',
  revoked: 'expired',
};

function daysLabel(days) {
  if (days < 0)  return { text: `Expired ${Math.abs(days)} days ago`, color: 'var(--danger)' };
  if (days <= 30) return { text: `Expires in ${days} days`, color: 'var(--danger)' };
  if (days <= 90) return { text: `Expires in ${days} days`, color: 'var(--warn)' };
  return { text: `Expires in ${days} days`, color: 'var(--accent)' };
}

const token = () => localStorage.getItem('token');
const accessCode = () => localStorage.getItem('lc_access_code');
const req = (url, opts = {}) => {
  const ac = accessCode();
  return fetch(url, {
    headers: {
      Authorization: `Bearer ${token()}`,
      'Content-Type': 'application/json',
      ...(ac ? { 'X-Access-Code': ac } : {}),
    },
    ...opts,
  }).then(r => r.json());
};

export default function HMO() {
  const [dashboard, setDashboard]   = useState(null);
  const [properties, setProperties] = useState([]);
  const [selected, setSelected]     = useState(null);
  const [licences, setLicences]     = useState([]);
  const [fireCerts, setFireCerts]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [tab, setTab]               = useState('licence');
  const [showModal, setShowModal]   = useState(false);
  const [showFireModal, setShowFireModal] = useState(false);
  const [saving, setSaving]         = useState(false);

  const emptyLicence = {
    licence_number: '', licence_type: 'mandatory',
    issuing_council: '', issue_date: '', expiry_date: '', notes: '',
  };
  const emptyFireCert = {
    cert_type: 'fire_risk_assessment', issue_date: '',
    expiry_date: '', notes: '',
  };
  const [form, setForm]         = useState(emptyLicence);
  const [fireForm, setFireForm] = useState(emptyFireCert);

  useEffect(() => {
    Promise.all([
      req('/api/hmo/dashboard'),
      req('/api/properties'),
    ]).then(([dash, props]) => {
      setDashboard(dash);
      setProperties(Array.isArray(props) ? props.filter(p => p.property_type === 'hmo' || p.is_hmo) : []);
    }).catch(err => {
      console.error('HMO load error:', err);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  async function selectProperty(p) {
    setSelected(p);
    const [lics, certs] = await Promise.all([
      req(`/api/properties/${p.id}/hmo-licences`),
      req(`/api/properties/${p.id}/certificates`),
    ]);
    setLicences(lics || []);
    setFireCerts((certs || []).filter(c =>
      ['fire_risk_assessment', 'fire_alarm_bs5839'].includes(c.cert_type)
    ));
  }

  async function saveLicence() {
    setSaving(true);
    try {
      await req(`/api/properties/${selected.id}/hmo-licences`, {
        method: 'POST', body: JSON.stringify(form),
      });
      const updated = await req(`/api/properties/${selected.id}/hmo-licences`);
      setLicences(updated || []);
      const dash = await req('/api/hmo/dashboard');
      setDashboard(dash);
      setShowModal(false);
      setForm(emptyLicence);
    } finally { setSaving(false); }
  }

  async function deleteLicence(id) {
    if (!window.confirm('Delete this licence record?')) return;
    await fetch(`/api/hmo-licences/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token()}` },
    });
    const updated = await req(`/api/properties/${selected.id}/hmo-licences`);
    setLicences(updated || []);
  }

  async function saveFireCert() {
    setSaving(true);
    try {
      await req(`/api/properties/${selected.id}/certificates`, {
        method: 'POST', body: JSON.stringify(fireForm),
      });
      const certs = await req(`/api/properties/${selected.id}/certificates`);
      setFireCerts((certs || []).filter(c =>
        ['fire_risk_assessment', 'fire_alarm_bs5839'].includes(c.cert_type)
      ));
      setShowFireModal(false);
      setFireForm(emptyFireCert);
    } finally { setSaving(false); }
  }

  if (loading) return <Spinner />;

  const tabs = [
    { id: 'licence', label: '📜 HMO Licence' },
    { id: 'fire',    label: '🔥 Fire Certificates' },
  ];

  return (
    <div className="fade-up">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 300 }}>HMO <em>Compliance</em></h1>
        <p style={{ color: 'var(--ink-3)', marginTop: 4 }}>Licence tracking and fire safety for Houses in Multiple Occupation</p>
      </div>

      {/* Dashboard summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Licences expiring (90 days)', value: dashboard?.licences_expiring_90_days, color: dashboard?.licences_expiring_90_days > 0 ? 'var(--warn)' : 'var(--accent)' },
          { label: 'Expired licences',            value: dashboard?.licences_expired,          color: dashboard?.licences_expired > 0 ? 'var(--danger)' : 'var(--accent)' },
          { label: 'HMOs missing licence',        value: dashboard?.hmo_missing_licence,       color: dashboard?.hmo_missing_licence > 0 ? 'var(--danger)' : 'var(--accent)' },
          { label: 'Fire certs expiring',         value: dashboard?.fire_certs_expiring,       color: dashboard?.fire_certs_expiring > 0 ? 'var(--warn)' : 'var(--accent)' },
        ].map(s => (
          <Card key={s.label} style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 32, fontFamily: 'var(--font-serif)', fontWeight: 300, color: s.color }}>{s.value ?? 0}</p>
            <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginTop: 4 }}>{s.label}</p>
          </Card>
        ))}
      </div>

      {/* Missing licence warning */}
      {dashboard?.hmo_missing_licence > 0 && (
        <Card style={{ marginBottom: 24, background: 'var(--danger-lt)', borderColor: 'var(--danger)' }}>
          <p style={{ fontWeight: 600, color: 'var(--danger)', fontSize: 15 }}>
            🚨 {dashboard.hmo_missing_licence} HMO propert{dashboard.hmo_missing_licence > 1 ? 'ies are' : 'y is'} missing an active licence
          </p>
          <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>
            Operating an HMO without a licence is a criminal offence — unlimited fine and tenants can reclaim up to 12 months' rent.
          </p>
        </Card>
      )}

      {/* No HMO properties */}
      {properties.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ fontSize: 32, marginBottom: 12 }}>🏢</p>
          <p style={{ fontWeight: 500, marginBottom: 8 }}>No HMO properties found</p>
          <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 16 }}>
            Add a property with type "HMO" in the Properties section to start tracking licences.
          </p>
          <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>
            A property is an HMO if 3 or more unrelated tenants share facilities (kitchen/bathroom).
            Mandatory licensing applies to 5+ tenants.
          </p>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '260px 1fr' : '1fr', gap: 24 }}>

          {/* Property list */}
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>HMO properties</p>
            {properties.map(p => {
              const hasLicence = licences.length > 0;
              return (
                <Card key={p.id} onClick={() => selectProperty(p)} style={{
                  marginBottom: 8, cursor: 'pointer',
                  borderColor: selected?.id === p.id ? 'var(--accent)' : 'var(--border)',
                }}>
                  <p style={{ fontWeight: 500, fontSize: 14 }}>{p.address_line1}</p>
                  <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>{p.city}, {p.postcode}</p>
                </Card>
              );
            })}
          </div>

          {/* Property detail */}
          {selected && (
            <div>
              <h2 style={{ fontSize: 20, marginBottom: 20 }}>{selected.address_line1}</h2>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
                {tabs.map(t => (
                  <button key={t.id} onClick={() => setTab(t.id)} style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
                    background: tab === t.id ? 'var(--accent)' : 'var(--border)',
                    color: tab === t.id ? '#fff' : 'var(--ink)',
                    fontWeight: tab === t.id ? 600 : 400,
                  }}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* ── LICENCE TAB ── */}
              {tab === 'licence' && (
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                    <div>
                      <h3 style={{ fontSize: 16 }}>HMO Licence</h3>
                      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                        HMO licences last 5 years · Renew before expiry · Criminal offence to operate without one
                      </p>
                    </div>
                    <Button size="sm" onClick={() => { setForm(emptyLicence); setShowModal(true); }}>+ Add licence</Button>
                  </div>

                  {licences.length === 0 ? (
                    <div style={{ background: 'var(--danger-lt)', border: '1px solid var(--danger)', borderRadius: 8, padding: '16px 20px' }}>
                      <p style={{ fontWeight: 600, color: 'var(--danger)', fontSize: 14 }}>⚠️ No licence recorded</p>
                      <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>
                        If this property has 5+ tenants from 2+ households, a mandatory HMO licence is required.
                        3-4 tenants may require an additional licence depending on your council.
                      </p>
                    </div>
                  ) : (
                    licences.map(l => {
                      const days = daysLabel(l.days_until_expiry);
                      return (
                        <div key={l.id} style={{ padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <p style={{ fontWeight: 600, fontSize: 15 }}>
                                  {LICENCE_TYPES.find(t => t.value === l.licence_type)?.label || l.licence_type}
                                </p>
                                <Badge status={STATUS_COLORS[l.status]} label={l.status} />
                              </div>
                              {l.licence_number && (
                                <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 2 }}>
                                  Licence No: <strong>{l.licence_number}</strong>
                                </p>
                              )}
                              {l.issuing_council && (
                                <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 2 }}>
                                  Issued by: {l.issuing_council}
                                </p>
                              )}
                              <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                                {l.issue_date && `Issued: ${l.issue_date} · `}
                                {l.expiry_date && `Expires: ${l.expiry_date}`}
                              </p>
                              {l.expiry_date && (
                                <p style={{ fontSize: 13, fontWeight: 600, color: days.color, marginTop: 4 }}>
                                  {days.text}
                                </p>
                              )}
                            </div>
                            <button onClick={() => deleteLicence(l.id)} style={{
                              fontSize: 12, color: 'var(--ink-3)', background: 'none',
                              border: '1px solid var(--border)', borderRadius: 6,
                              padding: '4px 10px', cursor: 'pointer',
                            }}>Delete</button>
                          </div>
                          {l.notes && <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8 }}>{l.notes}</p>}
                        </div>
                      );
                    })
                  )}

                  {/* Compliance info */}
                  <div style={{ marginTop: 20, background: 'var(--bg)', borderRadius: 8, padding: '14px 16px' }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>When do I need a licence?</p>
                    <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 4 }}>🔴 <strong>Mandatory:</strong> 5+ tenants from 2+ households sharing facilities</p>
                    <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 4 }}>🟡 <strong>Additional:</strong> 3-4 tenants — depends on your local council scheme</p>
                    <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>🟢 <strong>Selective:</strong> Some councils require all rental properties to be licensed</p>
                  </div>
                </Card>
              )}

              {/* ── FIRE CERTS TAB ── */}
              {tab === 'fire' && (
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                    <div>
                      <h3 style={{ fontSize: 16 }}>Fire Safety Certificates</h3>
                      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                        Fire Risk Assessment (annual) · Fire Alarm Test BS5839 (annual) · Both required for HMOs
                      </p>
                    </div>
                    <Button size="sm" onClick={() => { setFireForm(emptyFireCert); setShowFireModal(true); }}>+ Add certificate</Button>
                  </div>

                  {fireCerts.length === 0 ? (
                    <div style={{ background: 'var(--danger-lt)', border: '1px solid var(--danger)', borderRadius: 8, padding: '16px 20px' }}>
                      <p style={{ fontWeight: 600, color: 'var(--danger)', fontSize: 14 }}>⚠️ No fire certificates recorded</p>
                      <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>
                        HMOs require a written Fire Risk Assessment under the Fire Safety Order 2005,
                        and an annual fire alarm test certificate (BS5839).
                      </p>
                    </div>
                  ) : (
                    fireCerts.map(c => {
                      const days = c.expiry_date
                        ? daysLabel(Math.ceil((new Date(c.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)))
                        : null;
                      return (
                        <div key={c.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                            <div>
                              <p style={{ fontWeight: 500, fontSize: 14 }}>
                                {c.cert_type === 'fire_risk_assessment' ? '🔥 Fire Risk Assessment' : '🔔 Fire Alarm Test (BS5839)'}
                              </p>
                              <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>
                                {c.issue_date && `Issued: ${c.issue_date} · `}Expires: {c.expiry_date || '—'}
                              </p>
                              {days && <p style={{ fontSize: 13, fontWeight: 600, color: days.color, marginTop: 2 }}>{days.text}</p>}
                            </div>
                            <Badge
                              status={c.status === 'valid' ? 'valid' : c.status === 'expired' ? 'expired' : 'due_soon'}
                              label={c.status}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}

                  <div style={{ marginTop: 20, background: 'var(--bg)', borderRadius: 8, padding: '14px 16px' }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>HMO fire safety requirements</p>
                    <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 4 }}>🔥 Fire Risk Assessment — annually by a competent person</p>
                    <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 4 }}>🔔 Fire alarm test certificate (BS5839) — annually</p>
                    <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 4 }}>🚪 Fire doors — inspected regularly</p>
                    <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>📋 Interlinked smoke/heat detectors — Grade D minimum</p>
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Licence Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add HMO Licence">
        <FormField label="Licence type">
          <select value={form.licence_type} onChange={e => setForm(f => ({ ...f, licence_type: e.target.value }))}>
            {LICENCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </FormField>
        <FormField label="Licence number">
          <input value={form.licence_number} onChange={e => setForm(f => ({ ...f, licence_number: e.target.value }))} placeholder="e.g. HMO/2024/001234" />
        </FormField>
        <FormField label="Issuing council">
          <input value={form.issuing_council} onChange={e => setForm(f => ({ ...f, issuing_council: e.target.value }))} placeholder="e.g. London Borough of Southwark" />
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormField label="Issue date">
            <input type="date" value={form.issue_date} onChange={e => {
              const d = e.target.value;
              const expiry = d ? new Date(new Date(d).setFullYear(new Date(d).getFullYear() + 5)).toISOString().split('T')[0] : '';
              setForm(f => ({ ...f, issue_date: d, expiry_date: expiry }));
            }} />
          </FormField>
          <FormField label="Expiry date">
            <input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
            <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>Auto-set to 5 years from issue</p>
          </FormField>
        </div>
        <FormField label="Notes (optional)">
          <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Conditions, restrictions, contact details..." />
        </FormField>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button loading={saving} onClick={saveLicence}>Save licence</Button>
        </div>
      </Modal>

      {/* Add Fire Cert Modal */}
      <Modal open={showFireModal} onClose={() => setShowFireModal(false)} title="Add Fire Safety Certificate">
        <FormField label="Certificate type">
          <select value={fireForm.cert_type} onChange={e => setFireForm(f => ({ ...f, cert_type: e.target.value }))}>
            {CERT_TYPES_HMO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormField label="Issue date">
            <input type="date" value={fireForm.issue_date} onChange={e => {
              const d = e.target.value;
              const expiry = d ? new Date(new Date(d).setFullYear(new Date(d).getFullYear() + 1)).toISOString().split('T')[0] : '';
              setFireForm(f => ({ ...f, issue_date: d, expiry_date: expiry }));
            }} />
          </FormField>
          <FormField label="Expiry date">
            <input type="date" value={fireForm.expiry_date} onChange={e => setFireForm(f => ({ ...f, expiry_date: e.target.value }))} />
            <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>Auto-set to 1 year from issue</p>
          </FormField>
        </div>
        <FormField label="Notes (optional)">
          <textarea rows={2} value={fireForm.notes} onChange={e => setFireForm(f => ({ ...f, notes: e.target.value }))} />
        </FormField>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={() => setShowFireModal(false)}>Cancel</Button>
          <Button loading={saving} onClick={saveFireCert}>Save certificate</Button>
        </div>
      </Modal>
    </div>
  );
}
